import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    addReaction,
    createMessage,
    deleteMessage,
    listMessages,
    removeReaction,
    updateMessage,
} from '../../../service/ChatService';
import {mergeMessages, reconcileMessagePage} from '../chatUtils';
import {highestServerSequence, isServerMessage} from '../serverMessages';
import useMessageRead from './useMessageRead';
import useMessageNavigation from './useMessageNavigation';
import useMessageOutbox from './useMessageOutbox';
import useMessageMutations from './useMessageMutations';
import {createRefreshQueue} from '../refreshQueue';

export default function useConversationTimeline({
    conversation,
    currentUser,
    invalidation,
    messageMutationEvents,
    refreshConversations,
    readConversationMessages,
    cacheConversationMessages,
    onForbidden,
    cacheScope,
}) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const [newMessageNotice, setNewMessageNotice] = useState(false);
    const timelineRef = useRef(null);
    const atBottomRef = useRef(true);
    const messagesRef = useRef([]);
    const conversationId = conversation?.id || 0;
    const timelineScope = `${currentUser?.id || 0}:${conversationId}`;
    const owner = useMemo(() => ({key: timelineScope, mounted: true}), [timelineScope]);
    const activeScope = useRef(owner);
    activeScope.current = owner;
    useEffect(() => { owner.mounted = true; return () => { owner.mounted = false; }; }, [owner]);
    const messagesScope = useRef(owner);
    const refreshRequest = useRef(0);
    const [initialSnapshotOwner, setInitialSnapshotOwner] = useState(null);
    const outbox = useMessageOutbox({conversation, currentUser, onForbidden, confirmedMessages: messages, cacheScope});
    const sendOutbox = outbox.send;
    const fetchPage = useCallback(options => listMessages(conversationId, options), [conversationId]);
    const navigation = useMessageNavigation({owner, fetchPage, messagesRef, setMessages, setHasMore, setError, timelineRef});
    const {activeRef: historyActive, epoch: historyEpoch, returnToLatest} = navigation;
    const acknowledge = useMessageRead(timelineScope, conversationId, currentUser?.id, setError);
    const markRead = useCallback(async message => {
        const receipt = await acknowledge(message);
        if (receipt && owner.mounted && activeScope.current === owner) {
            setMessages(current => current.map(item => item.id === message.id ? {...item, read: true} : item));
        }
    }, [acknowledge, owner]);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    const scrollToBottom = useCallback(() => {
        if (historyActive.current) return;
        const timeline = timelineRef.current;
        if (timeline) timeline.scrollTop = timeline.scrollHeight;
        atBottomRef.current = true;
        setNewMessageNotice(false);
    }, [historyActive]);

    const refreshMessages = useCallback(async (preserve = true, signal) => {
        if (!conversationId || historyActive.current) return [];
        const navigationEpoch = historyEpoch.current;
        const generation = ++refreshRequest.current;
        const afterSequence = preserve ? highestServerSequence(messagesRef.current) : 0;
        const payload = await listMessages(conversationId, {afterSequence, signal});
        const incoming = [...(payload.messages || [])];
        const page = payload.messages || [];
        if (signal?.aborted || activeScope.current !== owner || !owner.mounted || generation !== refreshRequest.current || historyActive.current || historyEpoch.current !== navigationEpoch) return incoming;
        messagesScope.current = owner;
        setMessages(current => {
            const next = preserve ? mergeMessages(current, incoming) : reconcileMessagePage(current, incoming);
            void cacheConversationMessages?.(conversationId, next.filter(isServerMessage));
            return next;
        });
        if (!preserve) setHasMore(payload.has_more_before ?? page.length === 50);
        return incoming;
    }, [cacheConversationMessages, conversationId, owner, historyActive, historyEpoch]);

    useEffect(() => {
        if (!conversationId) {
            setMessages([]);
            return;
        }
        let active = true;
        const abortController = new AbortController();
        const initialQueue = createRefreshQueue(async (_, signal) => {
            const page = await refreshMessages(false, signal);
            if (active && !signal.aborted) { setInitialSnapshotOwner(owner); setError(null); setLoading(false); window.requestAnimationFrame(scrollToBottom); }
            return page;
        }, () => true, {retry: true});
        messagesScope.current = owner;
        setInitialSnapshotOwner(null);
        setMessages([]);
        setLoading(true);
        setError(null);
        atBottomRef.current = true;
        setNewMessageNotice(false);
        const cached = readConversationMessages?.(conversationId) || Promise.resolve([]);
        cached.then(cachedMessages => {
            if (active && !historyActive.current && cachedMessages.length) {
                setMessages(cachedMessages.filter(isServerMessage));
                setLoading(false);
                window.requestAnimationFrame(scrollToBottom);
            }
            if (!active) return [];
            return initialQueue.request(true, abortController.signal);
        }).then(() => {
            if (active) window.requestAnimationFrame(scrollToBottom);
        }).catch(loadError => {
            if (active && loadError?.name !== 'AbortError') setError(loadError);
        }).finally(() => active && setLoading(false));
        return () => {
            active = false;
            initialQueue.cancel();
            abortController.abort();
        };
    }, [conversationId, readConversationMessages, refreshMessages, scrollToBottom, owner, historyActive]);

    const eventRefreshQueue = useMemo(() => createRefreshQueue(async (_, signal) => {
        const page = await refreshMessages(true, signal);
        if (atBottomRef.current) window.requestAnimationFrame(scrollToBottom);
        else setNewMessageNotice(true);
        if (page.length === 50) queueMicrotask(() => { void eventRefreshQueue.request(true).catch(setError); });
        return page;
    }, () => true, {minIntervalMs: 200, retry: true}), [refreshMessages, scrollToBottom]);
    useEffect(() => () => eventRefreshQueue.cancel(), [eventRefreshQueue]);
    useEffect(() => {
        // Catch-up can arrive before the initial page. Let that page establish
        // pagination first, then drain the latest merged invalidation.
        if (initialSnapshotOwner === owner && invalidation && conversationId) void eventRefreshQueue.request(true).catch(refreshError => {
            if (refreshError?.name !== 'AbortError') setError(refreshError);
        });
    }, [invalidation, conversationId, eventRefreshQueue, initialSnapshotOwner, owner]);

    const refreshChangedMessage = useMessageMutations({events: messageMutationEvents, messages, messagesRef, setMessages, setError, owner, conversationId, actorId: currentUser?.id});

    useEffect(() => {
        if (historyActive.current || !conversationId || messagesScope.current !== owner || !messages.length || !cacheConversationMessages) return undefined;
        const timer = window.setTimeout(() => {
            if (!historyActive.current) void cacheConversationMessages(conversationId, messages.filter(isServerMessage));
        }, 150);
        return () => window.clearTimeout(timer);
    }, [cacheConversationMessages, conversationId, messages, owner, historyActive]);

    const loadOlder = useCallback(async () => {
        const navigationEpoch = historyEpoch.current;
        const first = messages.find(isServerMessage)?.sequence;
        if (!conversationId || !first) return;
        const timeline = timelineRef.current;
        const oldHeight = timeline?.scrollHeight || 0;
        try {
            const payload = await listMessages(conversationId, {beforeSequence: first});
            if (activeScope.current !== owner || !owner.mounted || historyEpoch.current !== navigationEpoch) return;
            const older = payload.messages || [];
            setMessages(current => mergeMessages(older, current));
            setHasMore(payload.has_more_before ?? older.length === 50);
            window.requestAnimationFrame(() => {
                if (timeline) timeline.scrollTop += timeline.scrollHeight - oldHeight;
            });
        } catch (loadError) {
            if (activeScope.current === owner && owner.mounted) setError(loadError);
        }
    }, [conversationId, messages, owner, historyEpoch]);

    const sendMessage = useCallback(async (body, attachments = [], quotedMessage = null, retryId, originalBody) => {
        if (!conversationId || !currentUser) return null;
        const clientId = retryId || crypto.randomUUID();
        const result = await sendOutbox({body, attachments, quotedMessage, clientId, originalBody}, () => createMessage(
            conversationId, body, clientId, attachments.map(attachment => attachment.id), quotedMessage?.id || null, originalBody,
        ));
        if (!result) return null;
        if (historyActive.current) { await returnToLatest(); return result; }
        setMessages(current => mergeMessages(current, [result.message]));
        window.requestAnimationFrame(scrollToBottom);
        return result;
    }, [conversationId, currentUser, sendOutbox, scrollToBottom, historyActive, returnToLatest]);

    const changeMessage = useCallback(async (message, action) => {
        try {
            await action();
            await refreshChangedMessage(message.id);
        } catch (actionError) {
            setError(actionError);
            throw actionError;
        }
    }, [refreshChangedMessage]);

    const actions = useMemo(() => ({
        edit: (message, body) => changeMessage(message, () => updateMessage(message.id, body, message.version)),
        remove: message => changeMessage(message, () => deleteMessage(message.id)),
        react: (message, emoji, reacted) => changeMessage(message, () => reacted ? removeReaction(message.id, emoji) : addReaction(message.id, emoji)),
        retry: message => sendMessage(message.body, message.attachments || [], message.quoted_message || null, message.client_message_id, message.original_body),
    }), [changeMessage, sendMessage]);

    const handleScroll = useCallback(event => {
        const element = event.currentTarget;
        const bottom = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
        atBottomRef.current = bottom;
        if (bottom) setNewMessageNotice(false);
    }, []);

    const visibleMessages = useMemo(() => [...messages, ...outbox.messages], [messages, outbox.messages]);
    return {
        navigation,
        messages: visibleMessages,
        storageWarning: outbox.warning,
        loading,
        hasMore,
        error,
        setError,
        newMessageNotice,
        timelineRef,
        refreshMessages,
        loadOlder,
        sendMessage,
        actions,
        markRead,
        handleScroll,
        scrollToBottom,
    };
}
